import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { msg } from 'api';

import BasketSummary from './BasketSummary';
import ItemsSummary from './ItemsSummary';
import TrolleyMenuItem from '../containers/TrolleyMenuItem';
import TrolleyMenuItemsDecorator from './TrolleyMenuItemsDecorator';

import { priceFormat } from 'apps/products/utils/priceFormat';

const groupItemsBySections = items => items.map(item => item.section)
  .reduce((acc, section) => (
    acc[section] === undefined
      ? Object.assign(acc, { [section]: items.filter(item => item.section === section) })
      : acc),
  {});

const getSectionName = section => (
  section.toUpperCase() === 'SUBSCRIPTIONS' ? msg.subscriptions.displayName : section
);

export default class TrolleyMenuItems extends PureComponent {
  static propTypes = {
    basket: PropTypes.shape({
      items: PropTypes.array.isRequired,
    }).isRequired,
    onRemoveAll: PropTypes.func.isRequired,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const nextItems = nextProps.basket.items;

    if (nextItems !== prevState.itemsProps) {
      const findNextItem = sku => nextItems.find(nextItem => nextItem.sku === sku);
      const findOldItem = sku => prevState.items.find(oldItem => oldItem.sku === sku);

      const oldItemsWithNewQuantity = prevState.items.map(oldItem => {
        const nextItem = findNextItem(oldItem.sku);
        if (nextItem) {
          // If nextItem is defined, it is in basket - either fully or optimistically.
          // For the second case, as Redux data can be very limited yet we need that spread.
          return { ...oldItem, ...nextItem };
        }
        // If nextItem is not there, item is not in basket. It shall stay in TrolleyMenu, but with 0 quantity.
        return { ...oldItem, quantity: 0 };
      });

      const newItems = nextItems.filter(nextItem => !findOldItem(nextItem.sku));

      return {
        items: [...newItems, ...oldItemsWithNewQuantity],
        itemsProps: nextItems,
      };
    }

    return null;
  }

  state = {
    items: this.props.basket.items,
  };

  render() {
    const { basket, onRemoveAll } = this.props;
    const { items } = this.state;

    const sections = groupItemsBySections(items);

    return (
      <div className="hd-trolleyMenu__items">
        <div className="hd-trolleyMenu__categories">
          {Object.keys(sections).map(section => (
            <div
              className={
                classNames('hd-trolleyMenu__category', `hd-trolleyMenu__category--${section.toLocaleLowerCase()}`)
              }
              key={section}
            >
              <h2 className="hd-trolleyMenu__categoryTitle">
                <span className="hd-trolleyMenu__categoryTitleLabel">{getSectionName(section)}</span>
              </h2>
              <TrolleyMenuItemsDecorator section={section} />
              <ul className="hd-trolleyMenu__items">
                {sections[section].map((item) => (
                  <TrolleyMenuItem
                    basketItem={item}
                    key={item.sku}
                    sku={item.sku}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>

        { items && items.length > 0 ? (
          <div className="hd-trolleyMenu__summary">
            <ItemsSummary
              basket={basket}
              items={items}
              onRemoveAll={onRemoveAll}
            />

            <div className="hd-trolleyMenu__discountsSummary">
              {basket.offerSavings > 0 ? (
                <p className="hd-trolleyMenu__discountsSummarySaving hd-trolleyMenu__discountsSummarySaving--offer">
                  Offer savings{' '}
                  <span className="hd-trolleyMenu__discountsSummarySavingValue">
                    -{priceFormat({ value: basket.offerSavings })}
                  </span>
                </p>
              ) : null}
              {basket.couponSavings > 0 ? (
                <p className="hd-trolleyMenu__discountsSummarySaving hd-trolleyMenu__discountsSummarySaving--coupons">
                  Coupons{' '}
                  <span className="hd-trolleyMenu__discountsSummarySavingValue">
                    -{priceFormat({ value: basket.couponSavings })}
                  </span>
                </p>
              ) : null}
              {basket.voucherSavings > 0 ? (
                <p className="hd-trolleyMenu__discountsSummarySaving hd-trolleyMenu__discountsSummarySaving--vouchers">
                  Vouchers{' '}
                  <span className="hd-trolleyMenu__discountsSummarySavingValue">
                    -{priceFormat({ value: basket.voucherSavings })}
                  </span>
                </p>
              ) : null}
            </div>

            <BasketSummary
              basket={basket}
              items={items}
            />
          </div>
        ) : null}
      </div>
    );
  }
}
